import { z } from 'zod';

import { customerLevelSchema, imageUrlSchema, slugSchema } from './store';

/**
 * Contrato dos eventos (RF-E2, RN-E1, RN-E5..RN-E7), entre a site-api, o site e
 * o back-office. Datas em ISO-8601 (UTC); o site e o back-office mostram-nas
 * em hora de Lisboa.
 *
 * Prefixos HTTP:
 *   - `/api/events/...`, `/api/me/events`  site publico (paginas e inscricoes);
 *   - `/api/site/events/...`               back-office (`events.manage`), so em gestao.
 */

/** Os tres papeis do roster (RF-E2). */
export const eventRoleSchema = z.enum(['visitor', 'volunteer', 'streamer']);
export type EventRole = z.infer<typeof eventRoleSchema>;

/**
 * - visitante: `confirmed` logo, ate a lotacao; depois `waitlisted` (RN-E6);
 * - voluntario e streamer: `pending` ate o staff decidir (`confirmed` ou `rejected`).
 * `cancelled` em qualquer papel.
 * - convite do staff (V39): `invited` ate a pessoa responder; `declined` se
 *   recusou. Aceitar passa ao estado normal do papel (o staff ja aprovou).
 */
export const registrationStatusSchema = z.enum([
  'invited',
  'declined',
  'pending',
  'confirmed',
  'waitlisted',
  'rejected',
  'cancelled',
]);
export type RegistrationStatus = z.infer<typeof registrationStatusSchema>;

export const eventStatusSchema = z.enum(['draft', 'published', 'cancelled']);

/**
 * Tipo de evento (GS17). `stream_party`: varios streamers jogam juntos, em
 * direto no canal da associacao. Formulario reduzido, online, so streamers.
 */
export const eventKindSchema = z.enum(['standard', 'stream_party']);
export type EventKind = z.infer<typeof eventKindSchema>;

/** O canal da Grow uP na Twitch, por omissao numa stream party. */
export const ASSOCIATION_CHANNEL = 'growupesportstv';

const channelSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_]{3,25}$/, 'o nome do canal na Twitch (3 a 25 letras, numeros ou _)');
export type EventStatus = z.infer<typeof eventStatusSchema>;

const datetimeSchema = z.string().datetime({ offset: true });
const optionalUrlSchema = z
  .string()
  .trim()
  .max(500)
  .nullable()
  .default(null)
  .transform((v) => (v === '' ? null : v))
  .refine((v) => v === null || /^https:\/\//.test(v), 'https://...');
const limitSchema = z.number().int().min(1).max(100_000).nullable().default(null);

// ---------------------------------------------------------------------------
// Dias de um evento
// ---------------------------------------------------------------------------

/** Um evento dura no maximo 31 dias: cada dia e uma caixa na inscricao. */
export const MAX_EVENT_DAYS = 31;

/** Um dia de calendario em Lisboa, `aaaa-mm-dd`. */
export const eventDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'aaaa-mm-dd');

const lisbonDay = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Lisbon',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** O dia de calendario em Lisboa de um instante, `aaaa-mm-dd`. */
export function lisbonDayOf(date: Date | string): string {
  return lisbonDay.format(new Date(date));
}

/**
 * Os dias de um evento: os dias de calendario em Lisboa entre o inicio e o
 * fim. Um evento que acaba a meia-noite nao conta o dia seguinte. E a mesma
 * conta que a migracao V11 faz em SQL.
 */
export function eventDays(startsAt: Date | string, endsAt: Date | string): string[] {
  const first = lisbonDayOf(startsAt);
  const last = lisbonDayOf(new Date(new Date(endsAt).getTime() - 1));
  const days: string[] = [];
  const cursor = new Date(`${first}T00:00:00Z`);
  // Com limite, para um fim muito longe nao dar uma lista sem fim.
  for (let day = first; day <= last && days.length <= MAX_EVENT_DAYS;) {
    days.push(day);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    day = cursor.toISOString().slice(0, 10);
  }
  return days.length === 0 ? [first] : days;
}

/**
 * O estado de um dia de uma inscricao:
 * - visitante: `confirmed` ou `waitlisted` (a lista de espera e por dia);
 * - voluntario e streamer: `confirmed` quer dizer que vem nesse dia (a
 *   aprovacao e da inscricao inteira).
 * `cancelled`: deixou de vir nesse dia.
 */
export const registrationDayStatusSchema = z.enum(['confirmed', 'waitlisted', 'cancelled']);
export type RegistrationDayStatus = z.infer<typeof registrationDayStatusSchema>;

export const myRegistrationDaySchema = z.object({
  day: z.string(),
  status: registrationDayStatusSchema,
  /** 1 = o proximo a entrar neste dia. So em `waitlisted`. */
  waitlistPosition: z.number().int().nullable(),
});
export type MyRegistrationDay = z.infer<typeof myRegistrationDaySchema>;

/** Os dias escolhidos: sem repetidos, pelo menos um. */
const chosenDaysSchema = z
  .array(eventDaySchema)
  .min(1, 'escolhe pelo menos um dia')
  .max(MAX_EVENT_DAYS)
  .refine((days) => new Set(days).size === days.length, 'dias repetidos');

// ---------------------------------------------------------------------------
// Site publico
// ---------------------------------------------------------------------------

/** `not_yet`: abre em `opensAt`; `closed`: fechou ou o evento ja comecou. */
export const registrationWindowStateSchema = z.enum(['not_yet', 'open', 'closed', 'cancelled']);
export type RegistrationWindowState = z.infer<typeof registrationWindowStateSchema>;

export const publicEventSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  venue: z.object({ name: z.string(), address: z.string(), mapUrl: z.string().nullable() }),
  status: z.enum(['published', 'cancelled']),
  cancelReason: z.string().nullable(),
  registration: z.object({
    state: registrationWindowStateSchema,
    opensAt: z.string().nullable(),
    closesAt: z.string(),
  }),
  /** Contagens, nunca nomes: o roster so se ve no back-office. */
  visitors: z.object({
    capacity: z.number().int().nullable(),
    /**
     * Lugares livres no dia com mais lugares; `null` sem limite. 0 = todos os
     * dias cheios: quem se inscrever fica em lista de espera.
     */
    spotsLeft: z.number().int().nullable(),
  }),
  /** Os dias do evento, por ordem. A lotacao de visitantes conta por dia. */
  days: z.array(
    z.object({
      day: z.string(),
      /** Lugares livres de visitante neste dia; `null` sem limite. */
      spotsLeft: z.number().int().nullable(),
    }),
  ),
  volunteersWanted: z.boolean(),
  streamersWanted: z.boolean(),
  kind: eventKindSchema.default('standard'),
  game: z.string().nullable().default(null),
  channel: z.string().nullable().default(null),
  /** Stream party: os streamers confirmados (so na pagina do evento). */
  participants: z
    .array(z.object({ nickname: z.string(), channel: z.string().nullable() }))
    .default([]),
  /**
   * Stream party: o alinhamento, a partir dos Turnos (blocos de hora certa).
   * Um bloco sem ninguem fica "por anunciar".
   */
  lineup: z
    .array(
      z.object({
        day: z.string(),
        startHour: z.number().int(),
        endHour: z.number().int(),
        nicknames: z.array(z.string()),
      }),
    )
    .default([]),
});
export type PublicEvent = z.infer<typeof publicEventSchema>;

/**
 * Duplicar um evento (GS17): fica um rascunho com tudo igual, o novo endereco
 * e a nova data de inicio (a duracao mantem-se); traz o Espaco e os Turnos,
 * sem pessoas.
 */
export const eventDuplicateSchema = z.object({
  slug: slugSchema,
  startsAt: datetimeSchema,
});
export type EventDuplicate = z.infer<typeof eventDuplicateSchema>;

export const eventListQuerySchema = z.object({
  when: z.enum(['upcoming', 'past']).default('upcoming'),
});

/** A inscricao de quem esta a ver (ou de um jovem seu). */
export const myRegistrationSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  role: eventRoleSchema,
  status: registrationStatusSchema,
  note: z.string(),
  /** 1 = o proximo a entrar (no dia em que esta mais perto). So em `waitlisted`. */
  waitlistPosition: z.number().int().nullable(),
  /** Os dias que escolheu, com o estado de cada um (os cancelados tambem). */
  days: z.array(myRegistrationDaySchema),
  streamerChannel: z.string().nullable(),
  decisionNote: z.string().nullable(),
  createdAt: z.string(),
  /** Jovem inscrito pelo encarregado (RN-E7); `null` se e a propria conta. */
  youth: z.object({ accountId: z.string().uuid(), login: z.string() }).nullable(),
  /** Convite do staff: os dias pedidos (`null` = todos). So em `invited`. */
  invitedDays: z.array(z.string()).nullable(),
  /** Veio de um convite do staff (aceite ou nao). */
  invited: z.boolean(),
});
export type MyRegistration = z.infer<typeof myRegistrationSchema>;

/**
 * Porque um papel nao esta disponivel:
 * - `closed`: inscricoes fechadas (ou o evento foi cancelado);
 * - `not_wanted`: este evento nao procura este papel;
 * - `not_member`: voluntario so para membros e associados (RN-E5);
 * - `not_streamer`: streamer so para streamers da associacao no Stream Portal (RN-E5);
 * - `portal_closed`: o Stream Portal ainda nao abriu;
 * - `youth_account`: contas de jovem nao entram no site;
 * - `registered`: ja ha uma inscricao ativa neste evento.
 */
export const roleUnavailableReasonSchema = z.enum([
  'closed',
  'not_wanted',
  'not_member',
  'not_streamer',
  'portal_closed',
  'youth_account',
  'registered',
]);
export type RoleUnavailableReason = z.infer<typeof roleUnavailableReasonSchema>;

export const roleOptionSchema = z.object({
  role: eventRoleSchema,
  available: z.boolean(),
  reason: roleUnavailableReasonSchema.nullable(),
  /** Visitante com todos os dias cheios: a inscricao vai para a lista de espera. */
  waitlist: z.boolean(),
});
export type RoleOption = z.infer<typeof roleOptionSchema>;

/** `GET /api/events/:slug/me`: o que a conta pode fazer neste evento. */
export const eventOptionsSchema = z.object({
  registration: myRegistrationSchema.nullable(),
  roles: z.array(roleOptionSchema),
  /** Jovens de quem a conta e encarregado: podem ir como voluntarios (RN-E7). */
  youths: z.array(
    z.object({
      accountId: z.string().uuid(),
      login: z.string(),
      /** Sem autorizacao ativa do encarregado, nao se inscreve. */
      authorizationActive: z.boolean(),
      registration: myRegistrationSchema.nullable(),
    }),
  ),
});
export type EventOptions = z.infer<typeof eventOptionsSchema>;

export const registerRequestSchema = z.object({
  role: eventRoleSchema,
  note: z.string().trim().max(500).default(''),
  /** Inscrever um jovem de quem se e encarregado, como voluntario (RN-E7). */
  youthAccountId: z.string().uuid().nullable().default(null),
  /** Os dias a que vai; `null` = todos os dias do evento. */
  days: chosenDaysSchema.nullable().default(null),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

/**
 * `PUT /api/events/registrations/:id/days`: mudar os dias de uma inscricao
 * ativa. Os dias que saem libertam o lugar; os que entram seguem a lotacao.
 * Para desistir de todos, cancela-se a inscricao.
 */
export const changeDaysRequestSchema = z.object({ days: chosenDaysSchema });
export type ChangeDaysRequest = z.infer<typeof changeDaysRequestSchema>;

/** `GET /api/me/events`: as minhas inscricoes e as dos meus jovens. */
export const myEventRegistrationSchema = myRegistrationSchema.extend({
  event: z.object({
    slug: z.string(),
    title: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    venueName: z.string(),
    status: z.enum(['published', 'cancelled']),
  }),
});
export type MyEventRegistration = z.infer<typeof myEventRegistrationSchema>;

// ---------------------------------------------------------------------------
// Back-office (Gestor de Eventos, `events.manage`)
// ---------------------------------------------------------------------------

export const eventInputSchema = z
  .object({
    slug: slugSchema,
    title: z.string().trim().min(1).max(120),
    summary: z.string().trim().max(300).default(''),
    description: z.string().trim().max(20_000).default(''),
    imageUrl: imageUrlSchema,
    startsAt: datetimeSchema,
    endsAt: datetimeSchema,
    kind: eventKindSchema.default('standard'),
    /** Stream party: o jogo e o canal (por omissao o da associacao). */
    game: z.string().trim().max(80).nullable().default(null),
    channel: channelSchema.nullable().default(null),
    /** Numa stream party fica "Online" sozinho. */
    venueName: z.string().trim().max(120).default(''),
    venueAddress: z.string().trim().max(300).default(''),
    mapUrl: optionalUrlSchema,
    /** Lotacao de visitantes; `null` = sem limite. */
    capacity: limitSchema,
    volunteerSlots: limitSchema,
    streamerSlots: limitSchema,
    volunteersWanted: z.boolean().default(true),
    streamersWanted: z.boolean().default(true),
    registrationOpensAt: datetimeSchema.nullable().default(null),
    /** `null` = fecha quando o evento comeca. */
    registrationClosesAt: datetimeSchema.nullable().default(null),
  })
  .refine((e) => new Date(e.endsAt) > new Date(e.startsAt), {
    message: 'o fim tem de ser depois do inicio',
    path: ['endsAt'],
  })
  .refine((e) => e.kind === 'stream_party' || e.venueName.length > 0, {
    message: 'escreve o local',
    path: ['venueName'],
  })
  .refine((e) => e.kind !== 'stream_party' || (e.game ?? '').length > 0, {
    message: 'escreve o jogo',
    path: ['game'],
  })
  .refine((e) => eventDays(e.startsAt, e.endsAt).length <= MAX_EVENT_DAYS, {
    message: `um evento dura no maximo ${String(MAX_EVENT_DAYS)} dias`,
    path: ['endsAt'],
  })
  .refine(
    (e) =>
      e.registrationOpensAt === null ||
      e.registrationClosesAt === null ||
      new Date(e.registrationClosesAt) > new Date(e.registrationOpensAt),
    { message: 'as inscricoes tem de fechar depois de abrir', path: ['registrationClosesAt'] },
  );
export type EventInput = z.infer<typeof eventInputSchema>;

const roleCountsSchema = z.object({
  pending: z.number().int(),
  confirmed: z.number().int(),
  waitlisted: z.number().int(),
});

export const adminEventSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  kind: eventKindSchema.default('standard'),
  game: z.string().nullable().default(null),
  channel: z.string().nullable().default(null),
  venueName: z.string(),
  venueAddress: z.string(),
  mapUrl: z.string().nullable(),
  capacity: z.number().int().nullable(),
  volunteerSlots: z.number().int().nullable(),
  streamerSlots: z.number().int().nullable(),
  volunteersWanted: z.boolean(),
  streamersWanted: z.boolean(),
  registrationOpensAt: z.string().nullable(),
  registrationClosesAt: z.string().nullable(),
  status: eventStatusSchema,
  publishedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  counts: z.object({
    visitor: roleCountsSchema,
    volunteer: roleCountsSchema,
    streamer: roleCountsSchema,
  }),
  /**
   * As mesmas contagens, dia a dia: quem vem em cada dia. A lotacao e as vagas
   * contam por dia.
   */
  days: z.array(
    z.object({
      day: z.string(),
      visitor: roleCountsSchema,
      volunteer: roleCountsSchema,
      streamer: roleCountsSchema,
    }),
  ),
});
export type AdminEvent = z.infer<typeof adminEventSchema>;

export const adminRegistrationSchema = z.object({
  id: z.string().uuid(),
  role: eventRoleSchema,
  status: registrationStatusSchema,
  note: z.string(),
  /** Quem vai. Num jovem so o login (RN-A16): nem email nem dados pessoais. */
  person: z.object({
    accountId: z.string().uuid(),
    login: z.string().nullable(),
    email: z.string().nullable(),
    isYouth: z.boolean(),
    /** Para a prioridade dos associados, gerida a mao (RN-E1). */
    level: customerLevelSchema.nullable(),
  }),
  /** O encarregado que inscreveu o jovem; `null` se a pessoa se inscreveu sozinha. */
  guardian: z
    .object({
      accountId: z.string().uuid(),
      login: z.string().nullable(),
      email: z.string().nullable(),
    })
    .nullable(),
  streamer: z.object({ streamerId: z.string().uuid(), channel: z.string().nullable() }).nullable(),
  waitlistPosition: z.number().int().nullable(),
  /** Os dias que escolheu, com a lista de espera e a presenca de cada um. */
  days: z.array(myRegistrationDaySchema.extend({ checkedInAt: z.string().nullable() })),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
  /** O check-in mais recente, em qualquer dia; o de cada dia esta em `days`. */
  checkedInAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  createdAt: z.string(),
  /** Convite do staff (V39): quem convidou e quando; `null` numa inscricao normal. */
  invite: z
    .object({
      by: z.string().nullable(),
      at: z.string(),
      /** Os dias do convite; `null` = todos. */
      days: z.array(z.string()).nullable(),
      answeredAt: z.string().nullable(),
    })
    .nullable(),
});
export type AdminRegistration = z.infer<typeof adminRegistrationSchema>;

export const registrationDecisionRequestSchema = z.object({
  decision: z.enum(['confirm', 'reject']),
  note: z.string().trim().max(300).nullable().default(null),
});
export type RegistrationDecisionRequest = z.infer<typeof registrationDecisionRequestSchema>;

/**
 * `POST /api/site/events/:id/invitations`: convidar uma conta que ja existe.
 * Num jovem o convite vai para o encarregado, e so como voluntario (RN-E7).
 */
export const inviteRequestSchema = z.object({
  accountId: z.string().uuid(),
  role: eventRoleSchema,
  /** Os dias pedidos; `null` = todos os dias do evento. */
  days: chosenDaysSchema.nullable().default(null),
  /** Vai no email do convite. */
  note: z.string().trim().max(500).default(''),
});
export type InviteRequest = z.infer<typeof inviteRequestSchema>;

/** Uma conta da pesquisa do convite (`GET /api/site/events/accounts?q=`). */
export const inviteAccountSchema = z.object({
  id: z.string().uuid(),
  login: z.string(),
  isYouth: z.boolean(),
});
export type InviteAccount = z.infer<typeof inviteAccountSchema>;

/** `POST /api/events/registrations/:id/answer`: aceitar ou recusar um convite. */
export const answerInviteRequestSchema = z.object({ accept: z.boolean() });
export type AnswerInviteRequest = z.infer<typeof answerInviteRequestSchema>;

/** Presenca num dia; `day` pode faltar num evento de um so dia. */
export const checkInRequestSchema = z.object({
  present: z.boolean(),
  day: eventDaySchema.nullable().default(null),
});

export const cancelEventRequestSchema = z.object({
  reason: z.string().trim().max(300).nullable().default(null),
});
